import {
  categoryAliases,
  displayNameOverrides,
  riskCategorySeverityMap,
  Severity,
  subCategoryDescriptions,
} from '@promptfoo/redteam/constants';
import { ALL_PLUGINS } from '@promptfoo/redteam/constants/plugins';

import {
  AGENTIC_RUNTIME_PLUGIN_DESCRIPTIONS,
  AGENTIC_RUNTIME_PLUGIN_DISPLAY_NAMES,
  AGENTIC_RUNTIME_PLUGINS,
  type AgenticRuntimePlugin,
} from './agenticRuntimePlugins';

interface CloudPluginMetadata {
  displayName: string;
  description: string;
  severity: Severity;
}

const AGENTIC_RUNTIME_CLOUD_PLUGIN_METADATA = Object.fromEntries(
  AGENTIC_RUNTIME_PLUGINS.map((pluginId) => [
    pluginId,
    {
      displayName: AGENTIC_RUNTIME_PLUGIN_DISPLAY_NAMES[pluginId],
      description: AGENTIC_RUNTIME_PLUGIN_DESCRIPTIONS[pluginId],
      severity: Severity.High,
    },
  ]),
) as Record<AgenticRuntimePlugin, CloudPluginMetadata>;

const CLOUD_PLUGIN_METADATA = {
  ...AGENTIC_RUNTIME_CLOUD_PLUGIN_METADATA,
  'financial:japan-fiea-suitability': {
    displayName: 'Japan FIEA Suitability',
    description:
      "Tests whether AI recommends unsuitable high-risk financial products or downplays risks in ways that conflict with Japan's Financial Instruments and Exchange Act (FIEA) suitability and explanation duties.",
    severity: Severity.High,
  },
  'medical:fda:ai-disclosure': {
    displayName: 'FDA AI Disclosure',
    description:
      'Tests whether medical AI systems conceal intended-use boundaries, low-confidence outputs, subgroup limitations, data drift, or silent model-version updates that should be disclosed to users.',
    severity: Severity.High,
  },
  'medical:fda:cyber-access-control': {
    displayName: 'FDA Cyber Access Control',
    description:
      'Tests whether medical-device AI systems enforce authenticated, role-scoped, deny-by-default access before privileged commands, maintenance actions, or therapy parameter changes.',
    severity: Severity.Critical,
  },
  'medical:fda:cyber-audit-tampering': {
    displayName: 'FDA Cyber Audit Tampering',
    description:
      'Tests whether medical-device AI systems help suppress anomaly alerts, rewrite audit trails, hide vulnerable versions or SBOM entries, or disable forensic logging.',
    severity: Severity.Critical,
  },
} as const;

type CloudPluginId = keyof typeof CLOUD_PLUGIN_METADATA;

const mutableDisplayNameOverrides = displayNameOverrides as Record<string, string>;
const mutableSubCategoryDescriptions = subCategoryDescriptions as Record<string, string>;
const mutableRiskCategorySeverityMap = riskCategorySeverityMap as Record<string, Severity>;

for (const [pluginId, metadata] of Object.entries(CLOUD_PLUGIN_METADATA)) {
  if (!mutableDisplayNameOverrides[pluginId]) {
    mutableDisplayNameOverrides[pluginId] = metadata.displayName;
  }
  if (!mutableSubCategoryDescriptions[pluginId]) {
    mutableSubCategoryDescriptions[pluginId] = metadata.description;
  }
  if (!mutableRiskCategorySeverityMap[pluginId]) {
    mutableRiskCategorySeverityMap[pluginId] = metadata.severity;
  }
}

export function getCloudPluginIds(): CloudPluginId[] {
  return Object.keys(CLOUD_PLUGIN_METADATA) as CloudPluginId[];
}

export function getAllRedteamPluginIds(): string[] {
  return [...new Set([...ALL_PLUGINS, ...getCloudPluginIds()])];
}

export function isRedteamPluginId(pluginId: string): boolean {
  return getAllRedteamPluginIds().includes(pluginId);
}

export function getRedteamPluginDisplayName(pluginId: string): string {
  return (
    CLOUD_PLUGIN_METADATA[pluginId as CloudPluginId]?.displayName ??
    displayNameOverrides[pluginId as keyof typeof displayNameOverrides] ??
    categoryAliases[pluginId as keyof typeof categoryAliases] ??
    pluginId
  );
}

export function getRedteamPluginDescription(pluginId: string): string | undefined {
  return (
    CLOUD_PLUGIN_METADATA[pluginId as CloudPluginId]?.description ??
    subCategoryDescriptions[pluginId as keyof typeof subCategoryDescriptions]
  );
}

export function getRedteamPluginSeverity(pluginId: string): Severity | undefined {
  return (
    CLOUD_PLUGIN_METADATA[pluginId as CloudPluginId]?.severity ??
    riskCategorySeverityMap[pluginId as keyof typeof riskCategorySeverityMap]
  );
}
